import {
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { ensureProtocol } from '@elfsight/lib-utils';
import { getScheduleSettings } from '@elfsight/widget-conditional-display';
import type { CountdownFinishHandler, TimerAPI } from '@elfsight/widget-ui';
import { createGCEventURLFromSchedule } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { ActionDuringCountdown } from '../../types';
import { useCountdownTimerState } from '../countdown-timer-state-context';
import { useFormPopup } from '../form-popup';
import { type ButtonProps, ElementsContext } from './elements-context';
import { ElementsThemeProvider } from './elements-theme-provider';

export const ElementsProvider = memo(({ children }: PropsWithChildren) => {
  const settings = useSettings();
  const {
    timerType,
    actionAfterCountdown,
    actionsDuringCountdown,
    headingText: settingsHeadingText,
    googleAnalytics,
    widgetId,
    editingItems,
    editMode = false
  } = settings;
  const { hideWidget, openWidget } = useCountdownTimerState();

  const {
    type: actionAfterCountdownType,
    messageText: messageAfterCountdown = settingsHeadingText,
    showTimer = true
  } = actionAfterCountdown ?? {};

  const [didCountdownFinish, setDidCountdownFinish] = useState(false);
  const isEditingMessage = editMode && !!editingItems?.['isEditingMessage'];
  const shouldShowMessage =
    actionAfterCountdownType === 'show-message' &&
    (didCountdownFinish || isEditingMessage);

  const timerRef = useRef<TimerAPI>(null);

  const handleCountdownFinish: CountdownFinishHandler = useCallback(
    (finishedOnInit) => {
      setDidCountdownFinish(true);
      if (!finishedOnInit) {
        sendGoogleAnalyticsEvents({
          googleAnalyticsEvents: googleAnalytics,
          eventType: 'countdownCompleted',
          valueMapping: {
            widgetId
          },
          editMode
        });
      }

      if (editMode || !actionAfterCountdown) {
        return;
      }

      if (actionAfterCountdownType === 'hide') {
        hideWidget();
      }

      if (actionAfterCountdownType === 'redirect') {
        const { redirectUrl } = actionAfterCountdown;
        window.open(ensureProtocol(redirectUrl), '_self');
      }
    },
    [
      actionAfterCountdownType,
      hideWidget,
      editMode,
      googleAnalytics,
      widgetId,
      actionAfterCountdown
    ]
  );

  const handleCountdownReset = useCallback(() => {
    setDidCountdownFinish(false);
    openWidget();
  }, [openWidget]);

  const handleCountdownRestart = useCallback(() => {
    sendGoogleAnalyticsEvents({
      googleAnalyticsEvents: googleAnalytics,
      eventType: 'countdownRestarted',
      valueMapping: {
        widgetId
      },
      editMode
    });

    timerRef.current?.reset();
  }, [googleAnalytics, widgetId, editMode]);

  const timer = useMemo(
    () => ({
      handleCountdownFinish,
      handleCountdownReset,
      handleCountdownRestart,
      ref: timerRef,
      isVisible: shouldShowMessage ? showTimer : true
    }),
    [
      handleCountdownFinish,
      handleCountdownReset,
      handleCountdownRestart,
      shouldShowMessage,
      showTimer
    ]
  );

  useEffect(() => {
    if (editMode && didCountdownFinish && timerType !== 'scheduled') {
      timerRef.current?.reset();
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [settingsHeadingText, editMode, actionsDuringCountdown, timerType]);

  const heading = useMemo(() => {
    if (shouldShowMessage) {
      return { text: messageAfterCountdown };
    }

    return { text: settingsHeadingText };
  }, [shouldShowMessage, messageAfterCountdown, settingsHeadingText]);

  const schedule = useMemo(() => getScheduleSettings(settings), [settings]);
  const { openPopup } = useFormPopup();
  const handleButtonClick = useCallback(
    (action: ActionDuringCountdown) => {
      const { type, buttonText } = action;
      sendGoogleAnalyticsEvents({
        googleAnalyticsEvents: googleAnalytics,
        eventType: 'buttonClicked',
        valueMapping: {
          widgetId,
          buttonType: type,
          buttonLabel: buttonText
        },
        editMode
      });

      if (type === 'open-form') {
        openPopup();
      }

      if (type === 'open-link') {
        const {
          buttonLink: { rawValue, target }
        } = action;
        if (rawValue) {
          window.open(rawValue, target);
        }
      }

      if (type === 'add-to-calendar') {
        const { eventTitle, eventDescription } = action;
        const url = createGCEventURLFromSchedule({
          title: eventTitle,
          description: eventDescription,
          schedule
        });

        window.open(url, '_blank');
      }
    },
    [schedule, openPopup, googleAnalytics, widgetId, editMode]
  );

  const buttons: ButtonProps[] = useMemo(() => {
    if (shouldShowMessage && actionAfterCountdown) {
      const { showButton, buttonLink, buttonText, buttonStyle } =
        actionAfterCountdown;
      const id = 'action-after-countdown';

      return showButton && buttonText
        ? [
            {
              id,
              onClick: () =>
                handleButtonClick({
                  id,
                  buttonLink,
                  buttonText,
                  buttonStyle,
                  type: 'open-link'
                }),
              text: buttonText,
              isPrimary: buttonStyle === 'primary'
            }
          ]
        : [];
    }

    return actionsDuringCountdown
      .map((action) => {
        const { id, buttonStyle, buttonText } = action;

        return {
          id,
          onClick: () => handleButtonClick(action),
          text: buttonText,
          isPrimary: buttonStyle === 'primary'
        };
      })
      .filter(({ text }) => !!text);
  }, [
    shouldShowMessage,
    actionsDuringCountdown,
    handleButtonClick,
    actionAfterCountdown
  ]);

  const context = useMemo(
    () => ({
      heading,
      buttons,
      timer
    }),
    [heading, buttons, timer]
  );

  return (
    <ElementsContext.Provider value={context}>
      <ElementsThemeProvider>{children}</ElementsThemeProvider>
    </ElementsContext.Provider>
  );
});
